import { useEffect, useMemo, useState, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import NotificationPreferencesCard from "./NotificationPreferencesCard";

type EmployeeProfilePanelProps = {
  onBack: () => void;
  fallbackName?: string;
  fallbackEmail?: string;
};

type ProfileData = {
  name?: string;
  email?: string;
  phone?: string;
  imageUrl?: string | null;
};

function getInitials(name: string, email: string) {
  const trimmed = name.trim();
  if (trimmed) {
    const pieces = trimmed.split(/\s+/).filter(Boolean);
    return pieces.slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");
  }
  if (email.trim()) return email.trim()[0]!.toUpperCase();
  return "U";
}

export default function EmployeeProfilePanel({
  onBack,
  fallbackName,
  fallbackEmail,
}: EmployeeProfilePanelProps) {
  const profile = useQuery((api as any).userProfiles.getMyProfile, {
    fallbackName: fallbackName || undefined,
    fallbackEmail: fallbackEmail || undefined,
  }) as ProfileData | undefined;
  const generateUploadUrl = useMutation((api as any).userProfiles.generateUploadUrl);
  const saveMyProfile = useMutation((api as any).userProfiles.saveMyProfile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<"details" | "routing">("details");
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!profile || isEditing) return;
    setName(profile.name || "");
    setPhone(profile.phone || "");
    setPreviewUrl(null);
    setSelectedImage(null);
    setRemoveImage(false);
  }, [profile, isEditing]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const displayName = (name || profile?.name || "").trim() || "Operator";
  const displayEmail = (profile?.email || "").trim() || "No email";
  const displayPhone = (phone || profile?.phone || "").trim() || "";

  const effectiveImageUrl = useMemo(() => {
    if (removeImage) return null;
    return previewUrl || profile?.imageUrl || null;
  }, [previewUrl, profile?.imageUrl, removeImage]);

  const uploadImage = async (file: File): Promise<Id<"_storage">> => {
    const uploadUrl = await generateUploadUrl();
    const res = await fetch(uploadUrl, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!res.ok) throw new Error("Upload failed");
    const payload = await res.json();
    if (!payload?.storageId) throw new Error("Missing storageId");
    return payload.storageId as Id<"_storage">;
  };

  const onImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Image files only"); return; }
    if (file.size > 8 * 1024 * 1024) { setError("Max 8 MB"); return; }
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setError(null);
    setSuccess(null);
    setRemoveImage(false);
    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const onSave = async () => {
    setError(null);
    setSuccess(null);
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName) { setError("Name is required"); return; }
    try {
      setIsSaving(true);
      let imageId: Id<"_storage"> | undefined;
      if (selectedImage) imageId = await uploadImage(selectedImage);
      await saveMyProfile({
        name: trimmedName,
        phone: trimmedPhone,
        ...(imageId ? { imageId } : {}),
        ...(removeImage ? { clearImage: true } : {}),
      });
      setIsEditing(false);
      setSelectedImage(null);
      setPreviewUrl(null);
      setRemoveImage(false);
      setSuccess("Profile updated successfully");
    } catch (err) {
      console.error(err);
      setError("Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const onCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    setSelectedImage(null);
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRemoveImage(false);
    setName(profile?.name || "");
    setPhone(profile?.phone || "");
  };

  const handleRemovePhoto = () => {
    setRemoveImage(true);
    setSelectedImage(null);
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className={`op-profile${mounted ? " op-profile--ready" : ""}`}>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={onImageSelected} hidden />

      <nav className="op-nav">
        <button type="button" className="op-nav__back" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <span className="op-nav__title">Operator Profile</span>
        {activeTab === "details" && !isEditing ? (
          <button type="button" className="op-nav__action" onClick={() => setIsEditing(true)} title="Edit profile">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
            </svg>
          </button>
        ) : <span />}
      </nav>

      <header className="op-hero">
        <div className="op-hero__grid" />
        <div className="op-hero__glow" />
        <div className="op-hero__inner">
          <button
            type="button"
            className={`op-avatar${isEditing ? " op-avatar--edit" : ""}`}
            onClick={() => isEditing && fileInputRef.current?.click()}
            disabled={!isEditing}
          >
            <div className="op-avatar__face">
              {effectiveImageUrl ? (
                <img src={effectiveImageUrl} alt={displayName} className="op-avatar__img" />
              ) : (
                <span className="op-avatar__initials">{getInitials(displayName, displayEmail)}</span>
              )}
            </div>
            {isEditing && (
              <div className="op-avatar__hover">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </div>
            )}
          </button>

          <div className="op-hero__meta">
            <h1 className="op-hero__name">{displayName}</h1>
            <span className="op-hero__email">{displayEmail}</span>
            <div className="op-hero__status">
              <span className="op-hero__dot" />
              Active
            </div>
          </div>
        </div>

        {isEditing && effectiveImageUrl && (
          <button type="button" className="op-hero__remove" onClick={handleRemovePhoto}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
            </svg>
            Remove photo
          </button>
        )}
      </header>

      {(error || success) && (
        <div className={`op-toast${error ? " op-toast--error" : " op-toast--ok"}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {error ? (
              <><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></>
            ) : (
              <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></>
            )}
          </svg>
          <span>{error || success}</span>
        </div>
      )}

      <div className="op-tabs">
        <button
          type="button"
          className={`op-tabs__item${activeTab === "details" ? " op-tabs__item--on" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Profile
        </button>
        <button
          type="button"
          className={`op-tabs__item${activeTab === "routing" ? " op-tabs__item--on" : ""}`}
          onClick={() => setActiveTab("routing")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 01-3.46 0"/>
          </svg>
          Alert Routing
        </button>
      </div>

      <main className="op-main">
        {activeTab === "details" ? (
          <section className="op-fields" key="details">
            <div className="op-field op-field--d1">
              <label className="op-field__label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Full Name
              </label>
              {isEditing ? (
                <input
                  className="op-field__input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  autoFocus
                />
              ) : (
                <p className="op-field__value">{displayName}</p>
              )}
            </div>

            <div className="op-field op-field--d2">
              <label className="op-field__label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
                Email Address
                <span className="op-field__badge">Managed</span>
              </label>
              <p className="op-field__value op-field__value--locked">{displayEmail}</p>
            </div>

            <div className="op-field op-field--d3">
              <label className="op-field__label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
                Phone Number
              </label>
              {isEditing ? (
                <input
                  className="op-field__input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                />
              ) : (
                <p className={`op-field__value${!displayPhone ? " op-field__value--empty" : ""}`}>
                  {displayPhone || "Not configured"}
                </p>
              )}
            </div>

            {isEditing && (
              <div className="op-actions">
                <button type="button" className="op-actions__btn op-actions__btn--ghost" onClick={onCancel} disabled={isSaving}>
                  Cancel
                </button>
                <button type="button" className="op-actions__btn op-actions__btn--primary" onClick={onSave} disabled={isSaving}>
                  {isSaving ? (
                    <><span className="op-spinner" />Saving</>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                        <path d="M17 21v-8H7v8M7 3v5h8"/>
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </section>
        ) : (
          <NotificationPreferencesCard key="routing" />
        )}
      </main>
    </div>
  );
}
